import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface LoginFormData {
    email: string;
    password: string;
    rememberMe: boolean;
}

interface RegisterFormData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
    role: 'student' | 'instructor';
    phone?: string;
    agreeToTerms: boolean;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState<'login' | 'register' | 'forgot'>('login');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loginData, setLoginData] = useState<LoginFormData>({
        email: '',
        password: '',
        rememberMe: false
    });
    const [registerData, setRegisterData] = useState<RegisterFormData>({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'student',
        phone: '',
        agreeToTerms: false
    });
    const [forgotEmail, setForgotEmail] = useState('');
    const [errors, setErrors] = useState<any>({});
    const [successMessage, setSuccessMessage] = useState('');

    const validateLogin = (): boolean => {
        const newErrors: any = {};

        if (!loginData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(loginData.email)) {
            newErrors.email = 'Please enter a valid email';
        }

        if (!loginData.password) {
            newErrors.password = 'Password is required';
        } else if (loginData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateRegister = (): boolean => {
        const newErrors: any = {};

        if (!registerData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!registerData.lastName.trim()) newErrors.lastName = 'Last name is required';

        if (!registerData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(registerData.email)) {
            newErrors.email = 'Please enter a valid email';
        }

        if (!registerData.password) {
            newErrors.password = 'Password is required';
        } else if (registerData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(registerData.password)) {
            newErrors.password = 'Password must contain uppercase, lowercase, and number';
        }

        if (registerData.password !== registerData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (!registerData.agreeToTerms) {
            newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateLogin()) return;

        setIsSubmitting(true);
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Store login data locally for demo
            localStorage.setItem('userSession', JSON.stringify({
                email: loginData.email,
                role: 'student', // This would come from API
                loginTime: new Date().toISOString()
            }));

            setSuccessMessage('Login successful! Redirecting to dashboard...');

            // Simulate redirect to dashboard
            setTimeout(() => {
                onClose();
                setSuccessMessage('');
                // Here you would redirect to the appropriate dashboard
                console.log('Redirect to dashboard');
            }, 1500);

        } catch (error) {
            setErrors({ general: 'Login failed. Please check your credentials.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateRegister()) return;

        setIsSubmitting(true);
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Store registration data locally for demo
            localStorage.setItem('registrationData', JSON.stringify({
                ...registerData,
                registrationTime: new Date().toISOString()
            }));

            setSuccessMessage('Registration successful! Please check your email to verify your account.');

            setTimeout(() => {
                setActiveTab('login');
                setSuccessMessage('');
            }, 2000);

        } catch (error) {
            setErrors({ general: 'Registration failed. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!forgotEmail.trim() || !/\S+@\S+\.\S+/.test(forgotEmail)) {
            setErrors({ email: 'Please enter a valid email address' });
            return;
        }

        setIsSubmitting(true);
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            setSuccessMessage('Password reset link sent to your email!');

            setTimeout(() => {
                setActiveTab('login');
                setSuccessMessage('');
                setForgotEmail('');
            }, 2000);

        } catch (error) {
            setErrors({ general: 'Failed to send reset email. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setActiveTab('login');
            setLoginData({ email: '', password: '', rememberMe: false });
            setRegisterData({
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                confirmPassword: '',
                role: 'student',
                phone: '',
                agreeToTerms: false
            });
            setForgotEmail('');
            setErrors({});
            setSuccessMessage('');
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    onClick={handleClose}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        className={`bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto ${activeTab === 'register' ? 'max-w-2xl' : 'max-w-md'
                            }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <img
                                        src="/images/swarsadhana-logo-animated.svg"
                                        alt="SwarSadhana Logo"
                                        className="h-10 w-10"
                                    />
                                    <span className="font-cinzel text-xl font-bold text-brand-dark-red">
                                        SwarSadhana
                                    </span>
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    disabled={isSubmitting}
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Tab Navigation */}
                            <div className="flex space-x-1 mt-6 bg-gray-100 rounded-lg p-1">
                                <button
                                    onClick={() => setActiveTab('login')}
                                    className={`flex-1 py-2 px-4 rounded-md font-poppins font-medium transition-all ${activeTab === 'login'
                                        ? 'bg-white text-brand-dark-red shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    Login
                                </button>
                                <button
                                    onClick={() => setActiveTab('register')}
                                    className={`flex-1 py-2 px-4 rounded-md font-poppins font-medium transition-all ${activeTab === 'register'
                                        ? 'bg-white text-brand-dark-red shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    Register
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {successMessage && (
                                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                                    {successMessage}
                                </div>
                            )}

                            {errors.general && (
                                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                                    {errors.general}
                                </div>
                            )}

                            {/* Login Form */}
                            {activeTab === 'login' && (
                                <form onSubmit={handleLogin} className="space-y-4">
                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            value={loginData.email}
                                            onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                                            className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.email ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="your@email.com"
                                            disabled={isSubmitting}
                                        />
                                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                    </div>

                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Password
                                        </label>
                                        <input
                                            type="password"
                                            value={loginData.password}
                                            onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                                            className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.password ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Enter your password"
                                            disabled={isSubmitting}
                                        />
                                        {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={loginData.rememberMe}
                                                onChange={(e) => setLoginData(prev => ({ ...prev, rememberMe: e.target.checked }))}
                                                className="rounded border-gray-300 text-brand-dark-red focus:ring-brand-dark-red"
                                                disabled={isSubmitting}
                                            />
                                            <span className="ml-2 font-poppins text-sm text-gray-600">Remember me</span>
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab('forgot')}
                                            className="font-poppins text-sm text-brand-dark-red hover:text-brand-gold transition-colors"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>

                                    <motion.button
                                        type="submit"
                                        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                                        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                                        disabled={isSubmitting}
                                        className={`w-full py-3 rounded-lg font-poppins font-semibold text-base transition-all ${isSubmitting
                                            ? 'bg-gray-400 cursor-not-allowed text-white'
                                            : 'bg-brand-dark-red hover:bg-red-800 text-white shadow-lg hover:shadow-xl'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <div className="flex items-center justify-center">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Signing In...
                                            </div>
                                        ) : (
                                            'Sign In'
                                        )}
                                    </motion.button>

                                    {/* Social Login */}
                                    <div className="mt-6">
                                        <div className="relative">
                                            <div className="absolute inset-0 flex items-center">
                                                <div className="w-full border-t border-gray-300" />
                                            </div>
                                            <div className="relative flex justify-center text-sm">
                                                <span className="px-2 bg-white text-gray-500">Or continue with</span>
                                            </div>
                                        </div>

                                        <div className="mt-4 grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                            >
                                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                                </svg>
                                                <span className="ml-2">Google</span>
                                            </button>

                                            <button
                                                type="button"
                                                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                            >
                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                                </svg>
                                                <span className="ml-2">Facebook</span>
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            )}

                            {/* Register Form */}
                            {activeTab === 'register' && (
                                <form onSubmit={handleRegister} className="space-y-4">
                                    {/* Role Selection */}
                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            I want to join as
                                        </label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setRegisterData(prev => ({ ...prev, role: 'student' }))}
                                                className={`p-3 border rounded-lg text-center transition-all ${registerData.role === 'student'
                                                    ? 'border-brand-dark-red bg-brand-dark-red/10 text-brand-dark-red'
                                                    : 'border-gray-300 hover:border-gray-400'
                                                    }`}
                                            >
                                                <div className="font-poppins font-medium">Student</div>
                                                <div className="text-xs text-gray-600">Learn new skills</div>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setRegisterData(prev => ({ ...prev, role: 'instructor' }))}
                                                className={`p-3 border rounded-lg text-center transition-all ${registerData.role === 'instructor'
                                                    ? 'border-brand-dark-red bg-brand-dark-red/10 text-brand-dark-red'
                                                    : 'border-gray-300 hover:border-gray-400'
                                                    }`}
                                            >
                                                <div className="font-poppins font-medium">Instructor</div>
                                                <div className="text-xs text-gray-600">Teach others</div>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                First Name
                                            </label>
                                            <input
                                                type="text"
                                                value={registerData.firstName}
                                                onChange={(e) => setRegisterData(prev => ({ ...prev, firstName: e.target.value }))}
                                                className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.firstName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="First name"
                                                disabled={isSubmitting}
                                            />
                                            {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                                        </div>

                                        <div>
                                            <label className="block font-poppins font-medium text-gray-700 mb-2">
                                                Last Name
                                            </label>
                                            <input
                                                type="text"
                                                value={registerData.lastName}
                                                onChange={(e) => setRegisterData(prev => ({ ...prev, lastName: e.target.value }))}
                                                className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.lastName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Last name"
                                                disabled={isSubmitting}
                                            />
                                            {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            value={registerData.email}
                                            onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                                            className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.email ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="your@email.com"
                                            disabled={isSubmitting}
                                        />
                                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                    </div>

                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Phone Number (Optional)
                                        </label>
                                        <input
                                            type="tel"
                                            value={registerData.phone || ''}
                                            onChange={(e) => setRegisterData(prev => ({ ...prev, phone: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all"
                                            placeholder="+91 9876543210"
                                            disabled={isSubmitting}
                                        />
                                    </div>

                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Password
                                        </label>
                                        <input
                                            type="password"
                                            value={registerData.password}
                                            onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                                            className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.password ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Create a strong password"
                                            disabled={isSubmitting}
                                        />
                                        {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                                    </div>

                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Confirm Password
                                        </label>
                                        <input
                                            type="password"
                                            value={registerData.confirmPassword}
                                            onChange={(e) => setRegisterData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                            className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Confirm your password"
                                            disabled={isSubmitting}
                                        />
                                        {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                                    </div>

                                    <div>
                                        <label className="flex items-start">
                                            <input
                                                type="checkbox"
                                                checked={registerData.agreeToTerms}
                                                onChange={(e) => setRegisterData(prev => ({ ...prev, agreeToTerms: e.target.checked }))}
                                                className="mt-1 rounded border-gray-300 text-brand-dark-red focus:ring-brand-dark-red"
                                                disabled={isSubmitting}
                                            />
                                            <span className="ml-2 font-poppins text-sm text-gray-600">
                                                I agree to the{' '}
                                                <a href="#" className="text-brand-dark-red hover:text-brand-gold">Terms of Service</a>
                                                {' '}and{' '}
                                                <a href="#" className="text-brand-dark-red hover:text-brand-gold">Privacy Policy</a>
                                            </span>
                                        </label>
                                        {errors.agreeToTerms && <p className="text-red-500 text-sm mt-1">{errors.agreeToTerms}</p>}
                                    </div>

                                    <motion.button
                                        type="submit"
                                        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                                        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                                        disabled={isSubmitting}
                                        className={`w-full py-3 rounded-lg font-poppins font-semibold text-base transition-all ${isSubmitting
                                            ? 'bg-gray-400 cursor-not-allowed text-white'
                                            : 'bg-brand-dark-red hover:bg-red-800 text-white shadow-lg hover:shadow-xl'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <div className="flex items-center justify-center">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Creating Account...
                                            </div>
                                        ) : (
                                            'Create Account'
                                        )}
                                    </motion.button>
                                </form>
                            )}

                            {/* Forgot Password Form */}
                            {activeTab === 'forgot' && (
                                <form onSubmit={handleForgotPassword} className="space-y-4">
                                    <div className="text-center mb-6">
                                        <h3 className="font-cinzel text-xl font-bold text-gray-900 mb-2">
                                            Reset Your Password
                                        </h3>
                                        <p className="font-poppins text-gray-600">
                                            Enter your email address and we'll send you a link to reset your password.
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block font-poppins font-medium text-gray-700 mb-2">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            className={`w-full px-4 py-3 border rounded-lg font-poppins focus:ring-2 focus:ring-brand-dark-red focus:border-transparent transition-all ${errors.email ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="your@email.com"
                                            disabled={isSubmitting}
                                        />
                                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                    </div>

                                    <motion.button
                                        type="submit"
                                        whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                                        whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                                        disabled={isSubmitting}
                                        className={`w-full py-3 rounded-lg font-poppins font-semibold text-base transition-all ${isSubmitting
                                            ? 'bg-gray-400 cursor-not-allowed text-white'
                                            : 'bg-brand-dark-red hover:bg-red-800 text-white shadow-lg hover:shadow-xl'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <div className="flex items-center justify-center">
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Sending Reset Link...
                                            </div>
                                        ) : (
                                            'Send Reset Link'
                                        )}
                                    </motion.button>

                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('login')}
                                        className="w-full py-2 font-poppins text-brand-dark-red hover:text-brand-gold transition-colors"
                                    >
                                        ← Back to Login
                                    </button>
                                </form>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default LoginModal; 
